import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import btnClose from '../../images/common/close.svg';
import updateWorkRecordTags from '../../actions/api/record/updateWorkRecordTags';
import {
  isTagUpdateButtonDisabled,
  handleTagChange,
} from './functions/updateTagDialogUtils';
import { TAGS_DIALOG_SELECT_TAB, PAGE_PATH } from '../../lib/constants';
import {
  Main,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import {
  Dialog,
  Tabs,
  Tab,
  TabPadding,
  UpdateBody,
  ClearBody,
  Lists,
  ListsTitle,
  ListsItem,
  Info,
  WarningMessage,
  Input,
  ButtonArea,
  CancelButton,
  UpdateButton,
  CheckContents,
  CheckArea,
  CheckboxWrapper,
  DefaultCheckbox,
  CheckboxLabel,
  TagSelect,
  AlertDeleteMessage,
} from './styles/UpdateTagsDialogStyles';
import useCheckUpdateHierarchyTag from './hooks/useCheckUpdateHierarchyTag';
import getTags from '../../actions/api/tag/getTags';
import useCheckDeleteTagsAndMemo from './hooks/useCheckDeleteTagsAndMemo';

const onSelectTab = (dispatch, tab) =>
  dispatch({ type: 'UPDATE_TAGS_SELECT_TAB', tab });

const createOptions = value => (
  <option value={value.id} key={value.id}>
    {value.name}
  </option>
);

/**
 * 階層タグ選択ボックスを表示するコンポーネント
 * @param {string} apiToken - 認証用のAPIトークン
 * @param {Function} dispatch - reduxのdispatch関数
 * @param {Function} formatMessage - react-intlのformatMessage関数
 * @param {Object} editOptions
 * @param {string} targetGroupId - 現在選択しているグループのID
 * @param {boolean} canUpdateHierarchyTag - 階層タグを更新できるかどうか
 * @returns
 */
const createHierarchyTagItems = (
  apiToken,
  dispatch,
  formatMessage,
  editOptions,
  targetGroupId,
  canUpdateHierarchyTag,
) => {
  const depthArray = [1, 2, 3, 4, 5];
  const { tags, ids } = editOptions;

  return (
    <>
      {depthArray.map(depth => {
        const optionLabel = formatMessage(
          { id: 'timeLine.updateTagsDialog.depth' },
          { depth },
        );
        const options = tags[depth];
        const id = ids[depth];
        const disabled =
          Object.keys(ids).length + 1 < depth ||
          !canUpdateHierarchyTag ||
          options === undefined;
        return (
          <Lists key={`tags_${depth}`}>
            <ListsTitle>{optionLabel}</ListsTitle>
            <ListsItem>
              <TagSelect
                disabled={disabled}
                value={id}
                onChange={e =>
                  handleTagChange(
                    e,
                    dispatch,
                    depth,
                    ids,
                    apiToken,
                    targetGroupId,
                  )
                }
              >
                <option value={-1} />
                {!disabled &&
                  options &&
                  options.map(value => createOptions(value))}
              </TagSelect>
            </ListsItem>
          </Lists>
        );
      })}
    </>
  );
};

/**
 * 削除するメモ(自由タグ)を表示するコンポーネント
 * @param {Object[]} timelines - 写真一覧画面で表示しているメディアの情報
 * @param {*} checkbox - 写真一覧画面でチェックボックスで選択しているメディアのID
 */
const displayDeleteMemoInTimeLinePage = (timelines, checkbox) =>
  timelines.map(timeline => {
    if (checkbox.ids.some(value => value === timeline.id) && timeline.freeTag) {
      return (
        <AlertDeleteMessage key={`memo_${timeline.id}`}>
          ・{timeline.freeTag}
        </AlertDeleteMessage>
      );
    }
    return null;
  });

/**
 * 削除するメモ(自由タグ)を表示するコンポーネント
 * 写真詳細画面からダイアログを開いた場合に使用
 * @param {*} selectedMediaInfo
 */
const displayDeleteMemoInMediaPage = selectedMediaInfo =>
  selectedMediaInfo.freeTag && (
    <AlertDeleteMessage>・{selectedMediaInfo.freeTag}</AlertDeleteMessage>
  );

/**
 * 削除予定のタグを表示するコンポーネント。
 * 写真一覧画面からダイアログを開いた場合に使用
 * @param {Object[]} timelines - 写真一覧画面で表示しているメディアの情報
 * @param {} checkbox - 写真一覧画面でチェックボックスで選択しているメディアのID
 * @returns
 */
const displayDeleteTagsInTimeLinePage = (timelines, checkbox) => {
  const deleteTags = [];
  timelines.forEach(timeline => {
    if (checkbox.ids.some(value => value === timeline.id)) {
      timeline.tags.forEach(tag => {
        deleteTags.push(tag);
      });
    }
  });

  return deleteTags.map((tag, index) => {
    const tagNames = Object.values(tag)
      .filter(value => value !== null)
      .map(value => value.name)
      .join(' > ');

    return (
      <AlertDeleteMessage key={`tag_${tag.id || index}`}>
        ・{tagNames}
      </AlertDeleteMessage>
    );
  });
};

/**
 * 削除予定のタグを表示するコンポーネント。
 * 写真詳細画面からダイアログを開いた場合に使用
 * @param {*} selectedMediaInfo
 * @returns
 */
const displayDeleteTagsInMediaPage = selectedMediaInfo => {
  const deleteTags = [];
  selectedMediaInfo.tags.forEach(tag => {
    deleteTags.push(tag);
  });

  return deleteTags.map((tag, index) => {
    const tagNames = Object.values(tag)
      .filter(value => value !== null)
      .map(value => value.name)
      .join(' > ');

    return (
      <AlertDeleteMessage key={`tag_${tag.id || index}`}>
        ・{tagNames}
      </AlertDeleteMessage>
    );
  });
};

/**
 * タグ編集ダイアログを表示するコンポーネント
 *
 * @param {*} checkbox - 写真一覧画面でチェックボックスで選択しているメディアのID
 * @returns
 */
const UpdateTagsDialog = ({ checkbox }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const location = useLocation();
  const isTimeLinePage = location.pathname.includes(PAGE_PATH.DS_TIMELINE);

  const apiToken = useSelector(state => state.main.apiToken);
  const editOptions = useSelector(state => state.timeline.editOptions);
  const { ids, freeTag } = editOptions;
  const clearCheckbox = useSelector(state => state.timeline.clearCheckbox);
  const selectedTab = useSelector(state => state.timeline.selectedTab);
  const targetGroupId = useSelector(state => state.main.targetGroupId);
  const selectedMediaInfo = useSelector(state => state.media.info); // 写真詳細画面から遷移した場合のみ使用

  // 表示している画面のURLに基づいてtimelinesを選択
  const timelines = useSelector(state => {
    if (location.pathname.includes(PAGE_PATH.DS_TIMELINELIST)) {
      return state.operatorWorks.lists.timelines;
    }
    return state.timeline.timelines;
  });

  const { hasMemo, hasTags } = useCheckDeleteTagsAndMemo(
    timelines,
    checkbox,
    selectedMediaInfo,
    isTimeLinePage,
  );

  const canUpdateHierarchyTag = useCheckUpdateHierarchyTag(
    timelines,
    checkbox,
    selectedMediaInfo,
  );

  const isDisabledUpdateButton = isTagUpdateButtonDisabled(
    selectedTab,
    freeTag,
    ids,
    clearCheckbox,
  );

  // 階層1の選択肢に表示する階層タグを取得
  useEffect(() => {
    dispatch(
      getTags(apiToken, {
        depth: 1,
        isEdit: true,
        isUsable: true,
        isDeleted: false,
        groupId: targetGroupId,
      }),
    );
  }, [apiToken, dispatch, targetGroupId]);

  return (
    <Main id="closeConfirmCompanyWindow" role="presentation">
      <Dialog role="presentation">
        <Contents>
          <Header>
            <FormattedMessage id="timeLine.updateTagsDialog.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => dispatch({ type: 'CLOSE_UPDATE_TAGS_DIALOG' })}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          <Tabs>
            <Tab
              id="selectUpdateTab"
              role="presentation"
              onClick={() =>
                onSelectTab(dispatch, TAGS_DIALOG_SELECT_TAB.update)
              }
              current={selectedTab === TAGS_DIALOG_SELECT_TAB.update}
            >
              <FormattedMessage id="timeLine.updateTagsDialog.updateTab" />
            </Tab>
            <TabPadding />
            <Tab
              id="selectClearTab"
              role="presentation"
              onClick={() =>
                onSelectTab(dispatch, TAGS_DIALOG_SELECT_TAB.clear)
              }
              current={selectedTab === TAGS_DIALOG_SELECT_TAB.clear}
            >
              <FormattedMessage id="timeLine.updateTagsDialog.clearTab" />
            </Tab>
          </Tabs>
          {selectedTab === TAGS_DIALOG_SELECT_TAB.update && (
            <UpdateBody>
              <Info>
                <FormattedMessage id="timeLine.updateTagsDialog.updateMsg1" />
                <br />
                <FormattedMessage id="timeLine.updateTagsDialog.updateMsg2" />
              </Info>
              <Lists>
                <ListsTitle>
                  <FormattedMessage id="timeLine.updateTagsDialog.freeTag" />
                </ListsTitle>
                <ListsItem>
                  <Input
                    type="text"
                    placeholder="複数入力はスペース区切り"
                    defaultValue={editOptions.freeTag}
                    onInput={e =>
                      dispatch({
                        type: 'UPDATE_FREE_TAG',
                        freeTag: e.target.value,
                      })
                    }
                  />
                </ListsItem>
              </Lists>
              {editOptions &&
                createHierarchyTagItems(
                  apiToken,
                  dispatch,
                  formatMessage,
                  editOptions,
                  targetGroupId,
                  canUpdateHierarchyTag,
                )}
              {!canUpdateHierarchyTag && (
                <WarningMessage>
                  <FormattedMessage id="timeLine.updateTagsDialog.errorMessage" />
                </WarningMessage>
              )}
            </UpdateBody>
          )}
          {selectedTab === TAGS_DIALOG_SELECT_TAB.clear && (
            <ClearBody>
              <Info>
                <FormattedMessage id="timeLine.updateTagsDialog.clearMsg1" />
                <br />
                <FormattedMessage id="timeLine.updateTagsDialog.clearMsg2" />
              </Info>
              <CheckContents>
                <CheckArea id="updateTagCheckAreaHead" role="presentation">
                  <CheckboxWrapper
                    id="updateTagCheckboxAreaHead"
                    role="presentation"
                    onClick={e => e.stopPropagation()}
                  >
                    <DefaultCheckbox
                      id="updateTagCheckboxHeader"
                      type="checkbox"
                      onChange={() =>
                        dispatch({
                          type: 'UPDATE_TAGS_CHECK_CHECKBOX',
                          checkFreeTag: !clearCheckbox.freeTag,
                        })
                      }
                      checked={clearCheckbox.freeTag ? 'checked' : ''}
                    />
                    <CheckboxLabel
                      className="timelineCheckboxLabel"
                      htmlFor="updateTagCheckboxHeader"
                    />
                  </CheckboxWrapper>
                  <label htmlFor="updateTagCheckboxHeader">
                    <FormattedMessage id="timeLine.updateTagsDialog.freeTag" />
                  </label>
                </CheckArea>
              </CheckContents>
              {isTimeLinePage && checkbox.ids.length === 1 && hasMemo && (
                <>
                  <WarningMessage>
                    <FormattedMessage id="timeLine.updateTagsDialog.clearWarning1" />
                  </WarningMessage>
                  {displayDeleteMemoInTimeLinePage(timelines, checkbox)}
                </>
              )}
              {!isTimeLinePage && hasMemo && (
                <>
                  <WarningMessage>
                    <FormattedMessage id="timeLine.updateTagsDialog.clearWarning1" />
                  </WarningMessage>
                  {displayDeleteMemoInMediaPage(selectedMediaInfo)}
                </>
              )}
              <CheckContents>
                <CheckArea id="clearTagCheckAreaHead" role="presentation">
                  <CheckboxWrapper
                    id="clearTagCheckboxAreaHead"
                    role="presentation"
                    onClick={e => e.stopPropagation()}
                  >
                    <DefaultCheckbox
                      id="clearTagCheckboxHeader"
                      type="checkbox"
                      onChange={() =>
                        dispatch({
                          type: 'UPDATE_TAGS_CHECK_CHECKBOX',
                          checkMasterTag: !clearCheckbox.masterTag,
                        })
                      }
                      checked={clearCheckbox.masterTag ? 'checked' : ''}
                    />
                    <CheckboxLabel
                      className="timelineCheckboxLabel"
                      htmlFor="clearTagCheckboxHeader"
                    />
                  </CheckboxWrapper>
                  <label htmlFor="clearTagCheckboxHeader">
                    <FormattedMessage id="timeLine.updateTagsDialog.masterTag" />
                  </label>
                </CheckArea>
              </CheckContents>
              {isTimeLinePage && checkbox.ids.length === 1 && hasTags && (
                <>
                  <WarningMessage>
                    <FormattedMessage id="timeLine.updateTagsDialog.clearWarning2" />
                  </WarningMessage>
                  {displayDeleteTagsInTimeLinePage(timelines, checkbox)}
                </>
              )}
              {!isTimeLinePage && hasTags && (
                <>
                  <WarningMessage>
                    <FormattedMessage id="timeLine.updateTagsDialog.clearWarning2" />
                  </WarningMessage>
                  {displayDeleteTagsInMediaPage(selectedMediaInfo)}
                </>
              )}
            </ClearBody>
          )}
          <ButtonArea>
            <CancelButton
              id="canselButtonClick"
              role="presentation"
              className="button"
              onClick={() => dispatch({ type: 'CLOSE_UPDATE_TAGS_DIALOG' })}
            >
              <FormattedMessage id="timeLine.updateTagsDialog.cancel" />
            </CancelButton>
            <UpdateButton
              id="updateButtonClick"
              role="presentation"
              className="button"
              disabled={isDisabledUpdateButton}
              onClick={() => {
                dispatch({ type: 'CLOSE_UPDATE_TAGS_DIALOG' });
                if (selectedTab === TAGS_DIALOG_SELECT_TAB.update) {
                  dispatch(
                    updateWorkRecordTags(apiToken, {
                      workRecordIds: checkbox?.ids,
                      tagIdDepth1: ids[1],
                      tagIdDepth2: ids[2],
                      tagIdDepth3: ids[3],
                      tagIdDepth4: ids[4],
                      tagIdDepth5: ids[5],
                      freeTag,
                      isUpdateTagIds: Object.keys(ids).length > 0,
                      isUpdateFreeTag: !!freeTag,
                    }),
                  );
                } else {
                  dispatch(
                    updateWorkRecordTags(apiToken, {
                      workRecordIds: checkbox?.ids,
                      tagIdDepth1: null,
                      tagIdDepth2: null,
                      tagIdDepth3: null,
                      tagIdDepth4: null,
                      tagIdDepth5: null,
                      freeTag: null,
                      isUpdateTagIds: clearCheckbox.masterTag,
                      isUpdateFreeTag: clearCheckbox.freeTag,
                    }),
                  );
                }
              }}
            >
              <FormattedMessage id="timeLine.updateTagsDialog.update" />
            </UpdateButton>
          </ButtonArea>
        </Contents>
      </Dialog>
    </Main>
  );
};

UpdateTagsDialog.propTypes = {
  checkbox: PropTypes.object.isRequired,
};

export default UpdateTagsDialog;
